/**
 * The gate in front of the shell.
 *
 * Presentation, not enforcement — the same note every guard in this app carries. RLS returns nothing and
 * the command functions refuse whether or not this component exists. What it adds is the right *message*
 * for each of the four states a signed-in person can be in, because they need four different next steps:
 *
 *   no session            → sign in
 *   session, no account   → accept the invitation
 *   account, not ACTIVE   → told so, plainly, with who to ask
 *   account, no ERP access→ told that too: it is a different thing to fix than a missing role
 *   account, needs aal2   → the second factor
 */
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import { useSession } from '../lib/session';
import { Button, LoadingRows, Panel } from '../ui/primitives';
import { AuthFrame } from './AuthFrame';
import { MfaGate } from './MfaGate';

export function RequireSession({ children }: { children: ReactNode }) {
  const { hasAuthSession, me, aal, isLoading, signOut } = useSession();
  const location = useLocation();

  if (hasAuthSession === false) {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />;
  }

  if (isLoading || !me) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper p-6">
        <Panel className="w-full max-w-md">
          <LoadingRows rows={3} columns={2} />
        </Panel>
      </div>
    );
  }

  if (!me.authenticated) {
    return <Navigate to="/accept-invitation" replace />;
  }

  if (me.status !== 'ACTIVE') {
    return <AccountBlocked status={me.status ?? 'UNKNOWN'} email={me.email} onSignOut={signOut} />;
  }

  if (me.erp_access === false) {
    return <AccountBlocked status="NO_ERP_ACCESS" email={me.email} onSignOut={signOut} />;
  }

  if (me.mfa_required && aal && aal.current !== 'aal2') {
    return <MfaGate />;
  }

  return <>{children}</>;
}

function AccountBlocked({ status, email, onSignOut }: { status: string; email?: string; onSignOut: () => Promise<void> }) {
  const explanation: Record<string, string> = {
    SUSPENDED: 'This account is suspended. An administrator can restore it from Administration → Users.',
    LOCKED: 'This account was locked after repeated failed sign-in attempts. An administrator must unlock it; there is no self-service unlock.',
    DEACTIVATED: 'This account has been deactivated. If you should still have access, ask an administrator to invite you again.',
    INVITED: 'This account has not been activated. Use the link in your invitation email to finish setting it up.',
    NO_ERP_ACCESS:
      'This account is active, but it has not been given access to the ERP application. That is separate from roles: an administrator grants it from Administration → Users → Applications. Field-app access is managed in those apps.',
  };

  return (
    <AuthFrame
      title="You cannot use the ERP right now"
      subtitle={email ? `Signed in as ${email}.` : undefined}
      aside="Every status change is recorded with who made it and why, so an administrator can tell you what happened."
    >
      <div className="flex items-start gap-3">
        <ShieldOff size={18} className="mt-0.5 shrink-0 text-refused" aria-hidden />
        <div>
          <p className="text-body">
            Account status: <span className="code-text">{status}</span>
          </p>
          <p className="mt-1 text-small text-ink-muted">
            {explanation[status] ?? 'This account is not active.'}
          </p>
        </div>
      </div>
      <div className="mt-5">
        <Button onClick={() => void onSignOut()}>Sign out</Button>
      </div>
    </AuthFrame>
  );
}
